import { api, type FleetStatusReport } from '../api';
import { toast } from '../ui';

/**
 * The fleet status report, as a CSV.
 *
 * Owners and authorities ask for this in a form they can open, not a screen
 * they have to be given a login for. CSV rather than PDF because the first
 * thing anyone does with it is put it in a spreadsheet, and because it needs no
 * rendering library to be correct.
 */
function csvCell(v: unknown): string {
  if (v === null || v === undefined) return '';
  const s = String(v);
  // Quote anything that would otherwise break the row, and double any quotes.
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

export function toCsv(report: FleetStatusReport): string {
  const header = [
    'Registration', 'Serial number', 'Type', 'Owner', 'Base', 'Regime', 'Status',
    'Airworthy', 'Reason not airworthy',
    'Airframe hours', 'Landings',
    'ARC form', 'ARC reference', 'ARC expires', 'ARC days left', 'Extensions used',
    'Programme basis', 'Programme revision', 'Programme review due',
    'Overdue items', 'Overdue detail', 'Due soon', 'Open defects', 'Grounding defects',
    'Next visit', 'Next visit date',
  ];

  const rows = report.aircraft.map((a) => [
    a.registration, a.serialNumber, a.type, a.owner, a.base, a.regime, a.status,
    a.airworthy ? 'yes' : 'no',
    a.airworthy ? '' : (a.groundedReason ?? a.overdueItems.join('; ')),
    a.tsnHours, a.landingsTotal,
    a.arcForm, a.arcReference,
    a.arcExpiresAt ? String(a.arcExpiresAt).slice(0, 10) : '',
    a.arcDaysLeft, a.arcExtensionsUsed,
    a.programmeBasis, a.programmeRevision,
    a.programmeReviewDue ? String(a.programmeReviewDue).slice(0, 10) : '',
    a.overdueCount, a.overdueItems.join('; '), a.dueSoonCount, a.openDefects, a.groundingDefects,
    a.nextVisit?.reference ?? '',
    a.nextVisit?.plannedStart ? String(a.nextVisit.plannedStart).slice(0, 10) : '',
  ]);

  return [header, ...rows].map((r) => r.map(csvCell).join(',')).join('\r\n');
}

/** Builds the report and hands it to the browser as a download. */
export async function exportFleetStatus(): Promise<void> {
  const report = await api.reports.fleetStatus();
  const blob = new Blob([toCsv(report)], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);

  const a = document.createElement('a');
  a.href = url;
  a.download = `fleet-status-${String(report.generatedAt).slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);

  const { summary } = report;
  toast(
    `${summary.total} aircraft — ${summary.airworthy} airworthy, ${summary.overdueItems} overdue item(s), ${summary.openDefects} open defect(s)`,
  );
}
